<template>
  <v-container fluid class="user-list-page">
    <v-card :loading="loading" outlined class="user-list-card">
      <div class="user-list-header">
        <div>
          <div class="page-title">Users</div>
          <div class="page-subtitle">
            {{ users.length }} user{{ users.length === 1 ? "" : "s" }} found
          </div>
        </div>

        <v-btn
          v-if="checkRightStatus(4)"
          small
          rounded
          color="primary"
          dark
          @click="add"
        >
          <v-icon left small>mdi-plus</v-icon>
          New User
        </v-btn>
      </div>

      <div class="user-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Search user"
          single-line
          hide-details
          clearable
          class="user-search"
        />
      </div>

      <v-card-text class="pa-4">
        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          item-key="id"
          :items="users"
          :search="search"
          :loading="loading"
          :sort-by="sortBy"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          class="user-table"
        >
          <template v-slot:item="{ item }">
            <tr class="pointer user-row">
              <td class="text-left">
                <div class="user-main">
                  <div class="user-avatar">
                    {{ getInitial(item.name) }}
                  </div>

                  <div class="user-info">
                    <div class="user-name">
                      {{ item.name || "Unknown User" }}

                      <v-tooltip bottom v-if="item.superAdmin">
                        <template v-slot:activator="{ on }">
                          <v-icon small color="amber accent-4" v-on="on">
                            mdi-crown
                          </v-icon>
                        </template>
                        <span>Super Admin</span>
                      </v-tooltip>

                      <v-tooltip bottom v-if="item.isAdmin && !item.superAdmin">
                        <template v-slot:activator="{ on }">
                          <v-icon small color="green accent-4" v-on="on">
                            mdi-account-circle
                          </v-icon>
                        </template>
                        <span>Admin Account</span>
                      </v-tooltip>
                    </div>

                    <div class="user-role">
                      {{ getUserRole(item) }}
                    </div>
                  </div>
                </div>
              </td>

              <td class="text-left">
                {{ item.email || "--" }}
              </td>

              <td class="text-left">
                {{ item.phoneNumber || "--" }}
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item
                      v-if="checkRightStatus(29)"
                      @click="edit(item)"
                    >
                      <v-list-item-title>
                        {{ $t("buttons.edit") }}
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="
                        checkRightStatus(28) &&
                        !item.superAdmin &&
                        currentUser.id !== item.id
                      "
                      @click="deleteUser(item)"
                    >
                      <v-list-item-title class="red--text">
                        {{ $t("buttons.delete") }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import userService from "../service.js";
import { mapGetters } from "vuex";

export default {
  name: "user-list",

  data() {
    return {
      user: null,
      headers: [
        {
          text: this.$t("labels.name"),
          value: "name",
          sortable: true,
          align: "left",
        },
        {
          text: this.$t("labels.emailAddress"),
          value: "email",
          sortable: true,
          align: "left",
        },
        {
          text: this.$t("labels.phoneNumber"),
          value: "phoneNumber",
          sortable: true,
          align: "left",
        },
        {
          text: "",
          value: "action",
          sortable: false,
          align: "right",
        },
      ],
      search: null,
      users: [],
      loading: true,
      page: 1,
      sortBy: "name",
      pageCount: 0,
      itemsPerPage: 10,
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },

  async created() {
    await this.getList();
    document.title = "smplsale - User List";
  },

  methods: {
    getInitial(name) {
      if (!name) {
        return "?";
      }

      return name.charAt(0).toUpperCase();
    },

    getUserRole(user) {
      if (user.superAdmin) {
        return "Super Admin";
      }

      if (user.isAdmin) {
        return "Admin";
      }

      return "User";
    },

    deleteUser(user) {
      if (!user.superAdmin) {
        if (
          user.id === this.currentUser.id ||
          this.currentUser.superAdmin ||
          this.currentUser.isAdmin
        ) {
          this.removeUser(user);
        } else {
          this.$swal(
            "Delete User",
            "You are not allow to delete other user account",
            "error"
          );
        }
      } else {
        if (this.currentUser.superAdmin) {
          this.removeUser(user);
        } else {
          this.$swal("Delete User", "Super admin can't be delete", "error");
        }
      }
    },

    removeUser(user) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.value) {
          this.$Progress.start();

          return userService
            .delete(user.id)
            .then((response) => {
              if (response.data) {
                this.$swal("Deleted!", "User has been deleted.", "success");
                this.$Progress.finish();
                this.getList();
              }
            })
            .catch((error) => {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "error",
                  title:
                    error && error.data && error.data.message
                      ? error.data.message
                      : "Unable to delete user",
                });

              this.$Progress.fail();
            });
        }
      });
    },

    add() {
      this.$router.push({ path: `/user/create` });
    },

    edit(user) {
      if (!user.superAdmin) {
        if (
          user.id === this.currentUser.id ||
          this.currentUser.superAdmin ||
          this.currentUser.isAdmin
        ) {
          this.$router.push({ path: `/user/edit/${user.id}` });
        } else {
          this.$swal(
            "Edit User",
            "You are not allow to edit someone else account",
            "error"
          );
        }
      } else {
        if (this.currentUser.superAdmin) {
          this.$router.push({ path: `/user/edit/${user.id}` });
        } else {
          this.$swal(
            "Edit User",
            "Super admin account can be only edit by super admin.",
            "error"
          );
        }
      }
    },

    async getList() {
      this.loading = true;

      try {
        const response = await userService.getAll();
        this.users = response.data || [];
      } catch (error) {
        console.error("user getList failed", error);
        this.users = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.user-list-page {
  background: #f8fafc;
}

.user-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.user-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.user-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-search {
  max-width: 340px;
  min-width: 260px;
}

.user-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.user-row:hover {
  background: #f8fafc;
}

.user-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.user-avatar {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 12px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.user-info {
  min-width: 0;
}

.user-name {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-role {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

@media (max-width: 600px) {
  .user-search {
    max-width: 100%;
    min-width: 100%;
  }

  .user-list-header {
    align-items: flex-start;
  }
}
</style>